import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export interface PasswordInputProps extends InputHTMLAttributes<HTMLInputElement> {
    /** Extra classes for the relative wrapper — put layout margins (e.g. `mt-1`)
     *  here so the eye toggle stays vertically centered on the field. */
    wrapperClassName?: string;
}

/**
 * Password field with a built-in show/hide eye toggle.
 *
 * Drop-in replacement for `<input type="password" .../>`: it forwards every
 * input prop and the caller's `className`, so each form keeps its own styling.
 * Move any layout margin (e.g. `mt-1`) onto `wrapperClassName`.
 *
 *   - Toggle is `type="button"` → it never submits the surrounding form.
 *   - Right padding is reserved via inline style, so the eye never overlaps the
 *     typed value no matter which padding class the field uses.
 *   - Keyboard-accessible: focusable, with an aria-label that reflects state.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    function PasswordInput({ className, wrapperClassName, style, disabled, ...props }, ref) {
        const [visible, setVisible] = useState(false);
        const label = visible ? 'Hide password' : 'Show password';

        return (
            <div className={cn('relative', wrapperClassName)}>
                <input
                    ref={ref}
                    {...props}
                    /* Component-controlled attrs come AFTER the spread so a caller
                     * can never override the masking via a stray `type`/`style`. */
                    type={visible ? 'text' : 'password'}
                    disabled={disabled}
                    className={className}
                    style={{ ...style, paddingRight: '2.75rem' }}
                />
                <button
                    type="button"
                    /* Toggle without stealing focus, so the caret stays in the field. */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setVisible((v) => !v)}
                    disabled={disabled}
                    aria-label={label}
                    title={label}
                    tabIndex={disabled ? -1 : 0}
                    className="focus-ring absolute inset-y-0 right-0 flex min-w-[2.75rem] items-center justify-center rounded-md px-3 text-ink-500 transition-colors hover:text-brand-navy disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                </button>
            </div>
        );
    },
);
PasswordInput.displayName = 'PasswordInput';
